import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./RecombinationCalculator.module.css";
import {
  calculateOutcomes,
  parseInput,
  parseToken,
  type Mod,
  type ParsedInput,
} from "../lib/recombination";

const ModToken: React.FC<{ mod: Mod }> = ({ mod }) => {
  const kindClasses = [
    mod.kind === "exclusive" ? styles.exclusive : "",
    mod.kind === "aspect" ? styles.aspect : "",
    mod.crafted ? styles.crafted : "",
  ].join(" ");
  return <span className={`${styles.mod} ${kindClasses}`}>{mod.id}</span>;
};

const ModList: React.FC<{ mods: Mod[]; empty: string }> = ({ mods, empty }) =>
  mods.length === 0 ? (
    <>{empty}</>
  ) : (
    <>
      {mods.map((mod, i) => (
        <React.Fragment key={i}>
          {i > 0 && " "}
          <ModToken mod={mod} />
        </React.Fragment>
      ))}
    </>
  );

const modFromToken = (token: string): Mod => parseToken(token);

const DEFAULT_INPUT = "p1p2s1 + p1p3s1";

export const RecombinationCalculator: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInput = searchParams.get("input") ?? DEFAULT_INPUT;
  const urlRemoveCrafted = searchParams.get("crafted") !== "0";

  // Local state drives the text box directly so the caret behaves normally
  // while typing (deriving the input's value from router state makes the
  // controlled value lag behind the keystroke, jumping the caret to the
  // end). The URL is mirrored in both directions so copying the address
  // bar shares the exact result.
  const [input, setInput] = useState(urlInput);
  const [removeCrafted, setRemoveCrafted] = useState(urlRemoveCrafted);

  // Local -> URL (user typed or toggled). Defaults are omitted from the URL
  // to keep shared links tidy.
  useEffect(() => {
    if (input === urlInput && removeCrafted === urlRemoveCrafted) return;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (input === DEFAULT_INPUT) {
          next.delete("input");
        } else {
          next.set("input", input);
        }
        if (removeCrafted) {
          next.delete("crafted");
        } else {
          next.set("crafted", "0");
        }
        return next;
      },
      { replace: true }
    );
  }, [input, removeCrafted, urlInput, urlRemoveCrafted, setSearchParams]);

  // URL -> local (shared link opened, back/forward, manual URL edit).
  useEffect(() => {
    setInput(urlInput);
    setRemoveCrafted(urlRemoveCrafted);
  }, [urlInput, urlRemoveCrafted]);

  const { parsed, outcomes, error } = useMemo(() => {
    try {
      const parsed: ParsedInput = parseInput(input);
      return {
        parsed,
        outcomes: calculateOutcomes(parsed, { removeCrafted }),
        error: null,
      };
    } catch (e) {
      return {
        parsed: null,
        outcomes: null,
        error: e instanceof Error ? e.message : String(e),
      };
    }
  }, [input, removeCrafted]);

  return (
    <div className={styles.container}>
      <h1>Recombination Odds</h1>
      <p className={styles.description}>
        Models{" "}
        <a href="https://www.poewiki.net/wiki/Recombinator">
          Unpredictable Recombination
        </a>
        . Enter the modifiers of the two input items as tokens:{" "}
        <code>p</code>/<code>s</code> for prefix/suffix, optional kind letters{" "}
        <code>e</code> (exclusive mod), <code>a</code> (aspect mod, non-native
        to both bases) and <code>c</code> (crafted mod, e.g. <code>pc1</code>,{" "}
        <code>pce1</code>), and a label — a number or a bracketed name such as{" "}
        <code>p[life]</code> or <code>pce[block]</code> — e.g.{" "}
        <code>p1p2s1 + p1p3s1</code>. Equal
        tokens are the same mod group; distinct tokens are distinct mod groups.
        Aspect mods count toward the pool but are never selected (padding);
        only one exclusive mod can appear on the output; crafted mods are
        calculated normally but never transfer to the output. The URL updates
        as you type — copy it from the address bar to share a result.
      </p>

      <input
        className={styles.input}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        aria-label="Recombination input"
        spellCheck={false}
        placeholder="p1p2s1 + p1p3s1"
      />

      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={removeCrafted}
          onChange={(e) => setRemoveCrafted(e.target.checked)}
        />{" "}
        Remove crafted mods (crafted rows with identical outputs are merged,
        summing their chances)
      </label>

      {error !== null ? (
        <p className={styles.error}>{error}</p>
      ) : parsed !== null && outcomes !== null ? (
        <>
          <p className={styles.pools}>
            {(() => {
              const both = [...parsed.item1, ...parsed.item2];
              const prefixes = both.filter((m) => m.slot === "prefix");
              const suffixes = both.filter((m) => m.slot === "suffix");
              return (
                <>
                  Prefix pool ({prefixes.length}): <ModList mods={prefixes} empty="(empty)" />
                  {" — "}
                  Suffix pool ({suffixes.length}): <ModList mods={suffixes} empty="(empty)" />
                </>
              );
            })()}
          </p>
          <table className={styles.resultsTable}>
            <thead>
              <tr>
                <th>Prefixes</th>
                <th>Suffixes</th>
                <th>Chance</th>
              </tr>
            </thead>
            <tbody>
              {outcomes.map((outcome, i) => (
                <tr key={i}>
                  <td>
                    <ModList mods={outcome.prefixes.map(modFromToken)} empty="(none)" />
                  </td>
                  <td>
                    <ModList mods={outcome.suffixes.map(modFromToken)} empty="(none)" />
                  </td>
                  <td>{(outcome.probability * 100).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
};
